import { useI18n } from "../i18n/I18nProvider";
import { localizeError } from "../auth/localizeError";
import { useEffect, useState, type FormEvent } from "react";
import { auth } from "../auth/client";
export function Mfa() {
  const { t, locale } = useI18n();
  const [factor, setFactor] = useState(""),
    [qr, setQr] = useState(""),
    [secret, setSecret] = useState(""),
    [code, setCode] = useState("");
  const [required, setRequired] = useState(true);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [verified, setVerified] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    auth.listMfa()
      .then((data) => {
        if (!alive) return;
        setRequired(data.required !== false);
        setFactor(data.factors.find(factor => factor.status === "verified")?.id || "");
        setVerified(data.aal === "aal2");
      })
      .catch((e) => {
        if (alive) setError(localizeError(e, locale));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  async function enroll() {
    setBusy(true);
    setError("");
    try {
      const data = await auth.enrollMfa();
      setFactor(data.factor_id);
      setQr(data.qr_code.startsWith("data:") ? data.qr_code : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(data.qr_code)}`);
      setSecret(data.secret);
    } catch (e) {
      setError(localizeError(e, locale));
    } finally {
      setBusy(false);
    }
  }
  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await auth.verifyMfa({
        factor_id: factor,
        code,
      });
      setVerified(true);
      setQr("");
      setSecret("");
      setCode("");
    } catch (e) {
      setError(localizeError(e, locale));
    } finally {
      setBusy(false);
    }
  }
  if (loading || !required) return null;
  return (
    <section className="settings-card">
      <h2>{t("双重验证", "Two-factor authentication")}</h2>
      <p className="muted">
        {t("管理操作需要动态验证码验证身份。请使用你的身份验证器应用。", "Admin actions require a verification code from your authenticator app.")}
      </p>
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <p>{t("正在检查验证状态…", "Checking verification status…")}</p>
      ) : verified ? (
        <p className="notice" role="status">
          {t("本次登录已完成双重验证，可以返回管理工作台。", "Two-factor authentication is complete for this session. You can return to the admin dashboard.")}
        </p>
      ) : factor ? (
        <form onSubmit={verify}>
          {qr && (
            <div className="mfa-enrollment">
              <img
                src={qr}
                width={180}
                height={180}
                alt={t("使用身份验证器扫描二维码", "Scan this QR code with your authenticator")}
              />
              <label>
                {t("无法扫码时，手动输入此密钥", "If you cannot scan the code, enter this key manually")}
                <input
                  readOnly
                  value={secret}
                  onFocus={(e) => e.target.select()}
                />
              </label>
            </div>
          )}
          <label>
            {t("动态验证码", "Verification code")}
            <input
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              required
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("6 位数字", "6 digits")}
            />
          </label>
          <button className="primary" disabled={busy}>
            {t("验证身份", "Verify identity")}
          </button>
        </form>
      ) : (
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void enroll()}
        >
          {t("设置身份验证器", "Set up authenticator")}
        </button>
      )}
    </section>
  );
}
